/**
 * remark-math only treats `$$` as display math when the fences sit on their own
 * lines, but `content/README.md` tells authors to write display math on one
 * line (`$$\psi(x) = \sqrt{2/L}$$`). This plugin promotes such a paragraph —
 * whose single child is `$$…$$` — to a display-math node, so the bank renders
 * the way it is authored. Use it after `remark-math`.
 *
 * The promoted node carries the same hast hints `mdast-util-math` puts on real
 * `$$`-block math (`<pre><code class="language-math math-display">`), which is
 * what `rehype-katex` looks for.
 */

interface MdastNode {
  type: string;
  value?: string;
  children?: MdastNode[];
  data?: Record<string, unknown>;
  position?: { start?: { offset?: number } };
}

interface MdastFile {
  value?: unknown;
}

function displayData(value: string): Record<string, unknown> {
  return {
    hName: "pre",
    hChildren: [
      {
        type: "element",
        tagName: "code",
        properties: { className: ["language-math", "math-display"] },
        children: [{ type: "text", value }],
      },
    ],
  };
}

/** True when this paragraph is exactly one `$$…$$` span written on one line. */
function oneLineDisplayMath(node: MdastNode, source: string): MdastNode | null {
  if (node.type !== "paragraph" || node.children?.length !== 1) return null;
  const only = node.children[0];
  if (!only || only.type !== "inlineMath") return null;
  const start = only.position?.start?.offset;
  if (typeof start !== "number" || source.slice(start, start + 2) !== "$$") return null;
  return only;
}

function promote(node: MdastNode, source: string): void {
  const children = node.children;
  if (!children) return;
  for (let i = 0; i < children.length; i += 1) {
    const child = children[i];
    if (!child) continue;
    const math = oneLineDisplayMath(child, source);
    if (math) {
      const value = math.value ?? "";
      children[i] = {
        type: "math",
        value,
        data: displayData(value),
        ...(child.position ? { position: child.position } : {}),
      };
      continue;
    }
    promote(child, source);
  }
}

/** Unified plugin: `remarkPlugins={[remarkMath, remarkInlineDisplayMath]}`. */
export function remarkInlineDisplayMath() {
  return (tree: MdastNode, file: MdastFile): void => {
    const source = typeof file.value === "string" ? file.value : "";
    if (!source.includes("$$")) return;
    promote(tree, source);
  };
}
